import {
  Accordion,
  Box,
  Button,
  Center,
  Container,
  createStyles,
  Group,
  Image,
  List,
  Stack,
  Text,
  Title,
} from "@mantine/core"
import { useScrollIntoView } from "@mantine/hooks"
import Link from "next/link"
import { useCallback } from "react"
import { brand } from "~/brand"
import { PrequalWrapper } from "~/prequal/components/PrequalWrapper"
import { Footer } from "./Footer"
import { Header } from "./Header"
import { ContactModal } from "./ContactModal"
import { QualifyButton } from "./QualifyButton"
import { ThemeWrapper } from "./ThemeWrapper"

const MoneyIcon = () => (
  <Box sx={{ width: "23px", height: "23px" }}>
    <svg
      preserveAspectRatio="xMidYMid meet"
      data-bbox="17 17 166 166"
      viewBox="17 17 166 166"
      xmlns="http://www.w3.org/2000/svg"
      data-type="color"
      role="presentation"
      aria-hidden="true"
    >
      <g>
        <path
          d="M100 17c-45.8 0-83 37.2-83 83s37.2 83 83 83 83-37.2 83-83-37.2-83-83-83zm1.7 118.3v6.7c0 1.8-1.5 3.2-3.3 3.2s-3.3-1.5-3.3-3.2v-6.8c-9.1-1.3-16.8-8.1-16.8-17.6 0-1.8 1.7-3.2 3.6-3.2s3.2 1.5 3.2 3.2c0 8.3 7.3 11.3 14.2 11.3 6.9 0 14.2-3 14.2-11.3 0-6.6-2.7-8.1-12.9-13.1-.9-.4-1.8-.9-2.8-1.4s-2-.9-2.8-1.3c-10-4.9-16.6-8.1-16.6-19 0-9.6 7.6-16.4 16.8-17.7v-6c0-1.8 1.5-3.2 3.3-3.2s3.3 1.5 3.3 3.2V65c11.6 1.1 17.8 7.9 17.8 17.8 0 1.8-1.2 3.2-3 3.2s-3.3-1.5-3.3-3.2c0-10.5-10.7-11.4-13.9-11.4-6.9-.1-14.3 2.9-14.3 11.3 0 6.6 2.7 8.1 12.9 13.1.9.4 1.7.9 2.7 1.4.9.5 1.8.9 2.7 1.3 10 4.9 16.4 8.1 16.4 19 0 9.9-6.5 16.7-18.1 17.8z"
          fill="#F7B64D"
          data-color="1"
        ></path>
      </g>
    </svg>
  </Box>
)

const useFAQStyles = createStyles((_theme, _params, getRef) => ({
  label: {
    position: "relative",
    height: "130px",

    [`&:hover .${getRef("index")}`]: {
      opacity: 1,
    },
  },

  indexBackground: {
    ref: getRef("index"),

    backgroundColor: brand.theme.yellow,
    opacity: 0.1,

    position: "absolute",
    width: "130px",
    height: "130px",
  },

  index: {
    width: "130px",
    height: "130px",
    position: "absolute",
  },
}))

const FAQLabel = ({ index, label }: { index: string; label: string }) => {
  const { classes } = useFAQStyles()

  return (
    <Group align="flex-start" noWrap={true} className={classes.label}>
      <Box>
        <Box className={classes.indexBackground}></Box>

        <Center className={classes.index}>
          <Text sx={{ fontSize: "25px" }}>{index}</Text>
        </Center>
      </Box>
      <Text sx={{ fontSize: "25px", alignSelf: "center", marginLeft: "150px" }}>{label}</Text>
    </Group>
  )
}

export const Home = () => {
  const { scrollIntoView, targetRef } = useScrollIntoView<HTMLDivElement>()

  const SamePageQualifyButton = useCallback(
    () => <QualifyButton onClick={() => scrollIntoView()} />,
    [scrollIntoView]
  )

  return (
    <ThemeWrapper>
      <Header qualifyButton={SamePageQualifyButton} />

      <ContactModal />

      <Container size="md" py="xl" px={0}>
        <Group spacing={0} position="center" align="flex-start">
          <Box sx={{ flexBasis: "517px" }}>
            <Text component="h1" weight={800} sx={{ fontSize: "52px" }}>
              Can you unlock your pension plan?
            </Text>
            <Title order={3} sx={{ fontWeight: "normal", fontSize: "24px" }} mb="xl">
              Do you have a locked-in pension plan and wish to access some or all the funds inside
              it?
            </Title>

            <SamePageQualifyButton />

            <Group align="flex-end" my="xl" ml="xl">
              <Box
                sx={{
                  fill: brand.theme.yellow,
                  transform: "scale(-1, 1) rotate(270deg)",
                  width: "50px",
                  height: "34px",
                }}
                ml="md"
              >
                <svg
                  preserveAspectRatio="none"
                  data-bbox="6.135 39.945 187.655 120.042"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="6.135 39.945 187.655 120.042"
                  role="presentation"
                  aria-hidden="true"
                >
                  <g>
                    <path d="M185.8 155.8c-28.2 7.8-50.7 4.4-81.7-7.7-20.4-7.9-50.5-26.2-67.5-47.8.2 4.4.8 8.8 1.7 13.3 4 19.2-25.4 27.3-29.4 8.1-5.1-24.1-3-47.4 5.1-70.6 2.8-7.8 10.4-12.9 18.7-10.6 21 5.6 42.7 14.1 60.8 26.1 16.3 10.7 1 37.1-15.4 26.3-5.5-3.6-11.2-6.8-17.2-9.7 12.7 19.4 44 39.2 59.2 44.8 18.2 6.7 44.3 9.9 63.9 11.2 15.2 1 10.2 14.3 1.8 16.6z"></path>
                  </g>
                </svg>
              </Box>
              <Text size="xl" mb={-20}>
                Get up to a $5,000 advance if you qualify!
              </Text>
            </Group>
          </Box>
          <Box mt={50}>
            <Image src="/img/landing1.webp" width="455" sx={{ maxWidth: "455px" }} alt="" />
          </Box>
        </Group>
      </Container>

      <Box sx={{ backgroundColor: brand.theme.yellow }} py="xl">
        <Container>
          <Title order={2} sx={{ color: "white", fontSize: "52px" }} mt="xl" mb="md">
            Welcome to Can You Unlock!
          </Title>
          <Text mb={70} weight="bold" sx={{ fontSize: "20px" }}>
            Canada&apos;s leading experts on pension unlocking. We specialize in finding solutions
            to give our clients access to their locked-in pension funds, even if you have been told
            &quot;no&quot; by your bank.
          </Text>
          <Group position="center" align="flex-start" mb={60}>
            <Stack align="center" sx={{ flexBasis: "30%" }}>
              <Image src="/img/icons-01.webp" width={155} height={155} alt="" />
              <Text size="lg" weight="bold" sx={{ textAlign: "center" }}>
                Unlock with confidence
              </Text>
              <Text sx={{ textAlign: "center" }}>
                10 years experience. Funds protected by the Canadian Deposit Insurance Corporation
                (CIDC).
              </Text>
            </Stack>
            <Stack align="center" sx={{ flexBasis: "30%" }}>
              <Image src="/img/icons-02.webp" width={155} height={155} alt="" />
              <Text size="lg" weight="bold" sx={{ textAlign: "center" }}>
                95% Success Rate
              </Text>
              <Text sx={{ textAlign: "center" }}>With our proven method.</Text>
            </Stack>
            <Stack align="center" sx={{ flexBasis: "30%" }}>
              <Image src="/img/icons-03.webp" width={155} height={155} alt="" />
              <Text size="lg" weight="bold" sx={{ textAlign: "center" }}>
                Advance of up to $5,000
              </Text>
              <Text sx={{ textAlign: "center" }}>
                Get an advance of up to $5,000 if you qualify!
              </Text>
            </Stack>
          </Group>

          <Box sx={{ backgroundColor: brand.theme.darkblue }} p={30}>
            <Text weight="bold" size="lg" sx={{ color: "white" }} mb={40}>
              We can get money out of the following type of plans:
            </Text>
            <Group position="apart" spacing="md">
              <List spacing="md">
                <List.Item icon={<MoneyIcon />} sx={{ color: "white" }}>
                  Locked-in Retirement Account (LIRA)
                </List.Item>
                <List.Item icon={<MoneyIcon />} sx={{ color: "white" }}>
                  Locked-in Retirement Savings Plan (LRSP)
                </List.Item>
              </List>
              <List spacing="md">
                <List.Item icon={<MoneyIcon />} sx={{ color: "white" }}>
                  Registered Pension Plan (RPP) / Union Plan
                </List.Item>
                <List.Item icon={<MoneyIcon />} sx={{ color: "white" }}>
                  Restricted Locked-in Savings Plan (RLSP)
                </List.Item>
              </List>
            </Group>
          </Box>
        </Container>
      </Box>

      <Box sx={{ backgroundColor: brand.theme.yellow }} py="xl">
        <Container mb="xl">
          <Text size="lg" mb="xl">
            So no matter what type of pension plan you have, and no matter your situation (even if
            you are not experiencing financial hardship), we most likely can help you as long as you
            no longer work for the employer that created the pension plan for you.
          </Text>
          <Text size="lg" mb="xl">
            Want to know if you qualify? Fill out our no-commitment assessment questionnaire. It
            only takes 2 minutes to complete and your results will be emailed to you. No personal
            information that could identify you such as social insurance number or your date of
            birth is required.
          </Text>

          <Text
            id="prequal"
            ref={targetRef}
            size="xl"
            mb="xl"
            py="xs"
            px="lg"
            weight="bold"
            sx={{ backgroundColor: "black", color: "white", display: "inline-block" }}
          >
            Answer the following quick questions to find out if we can help you or not.
          </Text>
        </Container>

        <Container size="sm" pb="xl">
          <Box sx={{ backgroundColor: "white" }} py="lg">
            <Title order={2} mb="xl" sx={{ textAlign: "center" }}>
              Pre-qualification questionnaire
            </Title>
            <PrequalWrapper />
          </Box>
        </Container>
      </Box>

      <Container>
        <Title order={2} sx={{ fontSize: "52px", color: brand.theme.yellow }} mt={40} mb="lg">
          Are my funds protected?
        </Title>

        <Text size="lg" weight="bold" mb="xl">
          How do I know that with this I will not lose my money?
        </Text>

        <Text pb="xl">
          All our Financial Institution Partners are properly registered and certified with the
          relevant and applicable government agencies. Your funds are also protected under the
          Canadian Deposit Insurance Corporation (CDIC).
        </Text>
        <Box sx={{ float: "left" }} mr="xl">
          <Image src="/img/landing2.webp" sx={{ maxWidth: "238px" }} height={235} alt="" />
        </Box>
        <Text pb="xl">
          Aside from this, there are several checks and balances in the process to ensure the safety
          of your funds, starting with your current financial institution. They will not transfer
          your plan unless the receiving institution is legally allowed to operate as an entity that
          may receive locked-in/registered plans.
        </Text>
        <Text pb="xl">
          The funds are transferred into a new account registered in your name. Therefore, once your
          funds are transferred, no one can access your funds or have them transferred or released
          without your consent.
        </Text>
        <Text pb="xl">
          You have the capacity to cancel the transaction at any time prior to receiving your final
          deposit. Basically, until the funds are released to you, you still have the right to
          terminate this agreement and have the funds returned to your original institution.
        </Text>
        <Text pb="xl">
          Additionally, there are severe fines and potential loss of licenses for all institutions
          involved in the process. As you can assume, this would be a critical blow to all the
          companies involved and we would not operate in a capacity that would lead to this.
        </Text>
        <Text pb="xl">
          Finally, you have us as another layer of protection. Since we are the broker/referral
          service, we work for you. The last thing we want is an unsatisfied client or a client that
          was not 100% happy with our services. Our reputation is our resume. Poor reviews,
          complaints online, and a negative social media presence would destroy our reputation and
          therefore our business. We pride ourselves on processing each account with Integrity,
          Efficiency, and Transparency.
        </Text>
      </Container>

      <Box py="xl">
        <Box
          sx={{
            width: "100wv",
            height: "608px",
            position: "relative",
            backgroundColor: brand.theme.yellow,
            backgroundImage: `url("/img/landing3.webp")`,
            backgroundPosition: "bottom center",
          }}
        >
          <Container sx={{ position: "relative", top: "50px" }}>
            <Title
              order={2}
              sx={{ color: "white", fontSize: "52px", maxWidth: "550px" }}
              mt={40}
              mb="lg"
            >
              Do you need more information first?
            </Title>
            <Text mb="lg" weight="bold" size="xl">
              Find out how it works!
            </Text>
            <Link href="/how-it-works" passHref>
              <Button radius={0} size="lg" color="cyan" sx={{ textTransform: "uppercase" }}>
                How it works
              </Button>
            </Link>
          </Container>
        </Box>
      </Box>

      <Container py="xl">
        <Title
          order={2}
          sx={{ fontSize: "52px", textAlign: "center", fontWeight: "normal" }}
          mb="xl"
        >
          Frequently asked questions
        </Title>
        <Accordion
          icon={null}
          styles={{
            control: {
              padding: 0,
              backgroundColor: brand.theme.lightyellow,
              "&:hover": {
                backgroundColor: brand.theme.lightyellow,
              },
            },
            content: {
              padding: "20px",
            },
          }}
          iconPosition="right"
        >
          <Accordion.Item
            label={<FAQLabel index={"01"} label="What is a locked-in pension plan?" />}
            mb="md"
          >
            A locked-in pension plan is basically the same as a RRSP (Registered Retirement Savings
            Plan) but with a few main differences:
            <List pl="xl">
              <List.Item>
                You can only open a locked-in pension plan if you leave your employer and you had a
                pension plan with them. By law the money that you and your employer paid into the
                plan must either (a) stay with the employer until you are eligible to receive a
                pension benefit or (b) the employer must give you the option to transfer the
                lump-sum value of the plan into a locked-in pension plan
              </List.Item>
              <List.Item>
                You can not add contributions (put more money) in a locked pension plan like you can
                in an RRSP.
              </List.Item>
            </List>
          </Accordion.Item>
          <Accordion.Item
            label={<FAQLabel index={"02"} label="What are the different types of pension plan?" />}
            mb="md"
          >
            <Text mb="md">
              <strong>LIRA (Locked-In Retirement Account):</strong> This plan falls under provincial
              jurisdiction. If your former employer was governed by the law of the province you
              worked in instead of federal law for pension plans, then you would have the option to
              put your retirement pension plan value in a LIRA upon leaving your employer.
            </Text>

            <Text mb="md">
              <strong>LRSP (Locked-In Retirement Savings Plan):</strong> Same as a LIRA but for
              employer whose pension plan was under federal law.
            </Text>

            <Text mb="md">
              <strong>RLSP (Registered Locked-In Savings Plan):</strong> This plan is under Federal
              jurisdiction and is used to transfer a one-time 50% unlocking from a LRSP account.
            </Text>

            <Text mb="md">
              <strong>LIF (Life Income Fund):</strong> Can be federal or provincial but this plan
              exist since that most other types of plan have a rule where the funds must be
              transferred into a LIF before you turn a certain age (normally 71) at which point you
              are legally obligated to take out a minimum amount every year.
            </Text>

            <Text mb="md">
              <strong>Registered Retirement Income Fund (RRIF):</strong> Similar to a LIF but for
              regular RRSP. Meaning the funds in your RRSP must be transferred to a RRIF before the
              end of the year that your turn 71.
            </Text>

            <Text mb="md">
              <strong>Prescribed Retirement Income Fund (PRIF):</strong> Only available for LIRAs
              that were under Manitoba or Sakatchewan rules. Similar to a LIF but with the major
              distinction that there is no cap on the amount of funds you can take out of them. You
              can therefore withdraw all of the funds in the same year if you wish to do so.
            </Text>

            <Text mb="md">
              <strong>Locked-in Retirement Income Fund (LRIF):</strong> Same as a LIF but exclusive
              to the province of Newfoundland &amp; Labrador.
            </Text>

            <Text mb="md">
              <strong>Registered Pension Plan (RPP):</strong> This is a pension plan while it is
              still with your employer. Once you leave the employer the RPP can remain with them
              until you are allowed to start receiving a retirement benefit or it can be converted
              to a LIRA or LRSP depending on whether your employer&apos;s is governed by provincial
              or federal law.
            </Text>
          </Accordion.Item>
          <Accordion.Item label={<FAQLabel index={"03"} label="Who are you?" />} mb="md">
            <Text mb="md">
              We operate as canyouunlock.com.Our administrative office is located at{" "}
              <strong>King Street West, Suite 2000, Toronto (ON), M5V 3S5.</strong>
            </Text>
            <Text mb="md">
              We are a lead generation referral site. In other words, we are the middleman between
              you and the financial institution able to assist in you in accessing your funds.
              It&apos;s our job to find and provide the financial institutions with a steady stream
              of suitable clients that have been pre-screened to see if they qualify for their
              services.
            </Text>
            <Text mb="md">
              It is important to understand that we do not share or match you with a service
              provider without getting your permission to do so. We will only request your
              permission to send your application once you are informed if you qualify for unlocking
              your plan or not.
            </Text>
            <Text mb="md">
              Think of us as a mortgage broker who matches you to the best organization that can
              provide the service you are looking for.
            </Text>
          </Accordion.Item>
          <Accordion.Item label={<FAQLabel index={"04"} label="How much do you charge?" />} mb="md">
            <Text mb="md">
              We do not charge you anything. If you do qualify and decide to use one of our service
              providers to help you get your funds, we will get a commission which is paid by the
              service provider &mdash; not you.
            </Text>
            <Text mb="md">
              The service provider will have their own fees and cost and rules which they will
              communicate with you and you will be informed of before agreeing to proceed.
            </Text>
          </Accordion.Item>
          <Accordion.Item
            label={<FAQLabel index={"05"} label="Who are your service providers?" />}
            mb="md"
          >
            <Text mb="md">
              We do not share the list of service providers for several reasons. The primary being
              that if we did, clients would simply go to them directly and we would not get our
              commission. Rest assured that once you are matched with a service provider that you
              will have full disclosure and they will want to communicate with you and explain their
              process, any other documentation they may need, and their applicable fees. You will
              also be able to ask them any questions you may have about the process or their company
              and you can always decide to not proceed if you feel that their service is not for
              you.
            </Text>
          </Accordion.Item>
          <Accordion.Item
            label={<FAQLabel index={"06"} label="If I pre-qualify, what will happen?" />}
            mb="md"
          >
            <Text mb="md">
              You will receive a confirmation email shortly after submitting your application that
              we have received your information.
            </Text>
            <Text mb="md">
              One of our advisors will review your information and send you an email with results of
              your assessment.
            </Text>
            <Text mb="md">
              Accessing funds in a locked-in pension plan is complicated and a major financial
              decision. For this reason, we will want to discuss with you how the process works, the
              costs and the pros and cons of the options available to you. If you decide to proceed,
              we will need to sign a legal contract and obtain additional information from you. We
              will provide you with an official application form to initiate the process.
            </Text>
            <Text mb="md">
              Your assigned advisor will guide you through the process step-by-step from A to Z and
              be there to assist you and answer any questions you may have.
            </Text>
          </Accordion.Item>
          <Accordion.Item
            label={
              <FAQLabel
                index={"07"}
                label="Why can't I skip the pre-qualification and apply right away?"
              />
            }
            mb="md"
          >
            <Text mb="md">
              Accessing your pension funds is a serious financial decision. We want to make sure
              that this service is a right fit for you, that you understand the pros and cons and
              that you give them due consideration before deciding to go ahead or not.
            </Text>
            <Text mb="md">
              We also have various options to provide this service and those options vary depending
              on your circumstances. Before we can give you an application, we must know which
              option is best for your situation as the information requested may vary.
            </Text>
            <Text mb="md">
              We are also obligated under the “know your client” (KYC) rules to communicate with you
              before providing this service to you. Under KYC, we have to do our due diligence and
              validate a number of things such as that you are the person who you claim to be, that
              the funds rightfully belong to you and that they were acquired from a valid financial
              activity.
            </Text>
          </Accordion.Item>
          <Accordion.Item
            label={<FAQLabel index={"08"} label="What types of plans qualify to be unlocked?" />}
            mb="md"
          >
            <Text mb="md">Here are the plans that can qualify to be unlocked:</Text>
            <List pl="xl">
              <List.Item>LIRA (Locked-In Retirement Account)</List.Item>
              <List.Item>LRSP (Locked-In Retirement Savings Plan)</List.Item>
              <List.Item>RLSP (Registered Locked-In Savings Plan)</List.Item>
              <List.Item>Registered Pension Plan (RPP)</List.Item>
            </List>
          </Accordion.Item>
          <Accordion.Item
            label={
              <FAQLabel index={"09"} label="What types of plans do not qualify to be unlocked?" />
            }
            mb="md"
          >
            <List>
              <Text mb="md">
                If your pension plan falls under the following situations such then we can not
                assist you:
              </Text>

              <List pl="xl">
                <List.Item>Any pension plans from Quebec or Saskatchewan</List.Item>
                <List.Item>
                  Canada Pension Plan pension benefit (Retirement, Disability, Survivor or
                  dependent&apos;s pension)
                </List.Item>
                <List.Item>Old Age Security program (OAS &amp; GIC)</List.Item>
                <List.Item>Provincial government benefit program (Disability)</List.Item>
                <List.Item>Life annuity Insurance settlement annuity</List.Item>
                <List.Item>
                  An employer&amp;s pension plan from which you are already receiving a monthtly
                  pension benefit amount.
                </List.Item>
                <List.Item>
                  A LIF (Life Income Fund), Prescribed Retirement Income Fund (PRIF) or Locked-in
                  Retirement Income Fund (LRIF)
                </List.Item>
              </List>
            </List>
          </Accordion.Item>
        </Accordion>
      </Container>

      <Footer qualifyButton={SamePageQualifyButton} />
    </ThemeWrapper>
  )
}
